/**
 * A target's level in the hierarchy, counted from the bottom: the deepest
 * thing in the whole hierarchy — always a screen — is level 0, and the root is
 * the highest, the number of steps down to that deepest screen.
 *
 *   All Screens (3) › Road (2) › Digital (1) › a screen (0)
 *
 * The top is fixed by the deepest branch, so a shallower branch simply never
 * reaches 0: a screen straight under Road above would be level 1. That needs
 * the whole hierarchy processed first, which is what this does.
 */
export type HierarchyLevelNode = {
	id: number;
	parentId: number | null;
	/** Does this node carry screens of its own — a level below it? */
	hasAssets: boolean;
};

export const hierarchyLevels = (nodes: HierarchyLevelNode[]) => {
	const byId = new Map(nodes.map((node) => [node.id, node]));
	const depths = new Map<number, number>();

	/** Steps down from the root: the root is 0. Walked iteratively, so a deep tree cannot overflow the stack. */
	const depthOf = (id: number): number => {
		const path: number[] = [];
		let current = byId.get(id);

		while (current && !depths.has(current.id)) {
			path.push(current.id);
			current = current.parentId !== null ? byId.get(current.parentId) : undefined;
		}

		let depth = current ? (depths.get(current.id) as number) : -1;

		for (let i = path.length - 1; i >= 0; i--) {
			depth += 1;
			depths.set(path[i], depth);
		}

		return depths.get(id) ?? 0;
	};

	const deepest = nodes.reduce(
		(max, node) => Math.max(max, depthOf(node.id) + (node.hasAssets ? 1 : 0)),
		0
	);
	const top = deepest;

	return {
		/** The root's level — the highest there is. */
		top,
		nodeLevel: (id: number) => top - depthOf(id),
		/** A screen sits one level below the node carrying it. */
		assetLevel: (nodeId: number) => top - depthOf(nodeId) - 1,
	};
};
